import React, { useState, useEffect } from 'react';
import { X, Shuffle, Users, Target, BarChart3, UserCheck, UserX } from 'lucide-react';
import { INJURY_STATUS } from '../constants/injuries';
import { InjuryBadge } from './ui/InjuryBadge';
import type { InjuryStatus } from '../constants/injuries';

interface RandomizationSettings {
  preserveColors: boolean;
  ensureFairPlayTime: boolean;
  showPreview: boolean;
  protectedPositions: { [inning: number]: { [position: string]: string } };
  standardProtectedPositions: { [position: string]: string }; // For non-baseball sports
  absentPlayerIds?: string[];
}

interface RandomizationPreview {
  willRandomize: number;
  willBench: number;
  strategy: string;
}

interface RandomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isRandomizing: boolean;
  settings: RandomizationSettings;
  onSettingsChange: (settings: Partial<RandomizationSettings>) => void;
  preview: RandomizationPreview | null;
  title: string;
  description: string;
  sport: string;
  activeTab: string;
  players: any[];
  positions: any[];
  teamInnings: number;
}

const RandomizationModal: React.FC<RandomizationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isRandomizing,
  settings,
  onSettingsChange,
  preview,
  title,
  description,
  sport,
  activeTab,
  players = [],
  positions = [],
  teamInnings
}) => {
  const [showProtectionSettings, setShowProtectionSettings] = useState(false);
  
  const isBaseballFielding = (sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding';
  const isBaseballLineup = (sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup';
  const isStandardSport = !isBaseballFielding && !isBaseballLineup;

  // Initialize protection settings and auto-mark "Out" players as absent when modal opens
  useEffect(() => {
    if (isOpen) {
      // Auto-mark players with "Out" injury status as absent
      const outPlayerIds = players
        .filter(player => player.injury_status === INJURY_STATUS.OUT)
        .map(player => player.id);
      
      const currentAbsent = settings.absentPlayerIds || [];
      const newAbsentIds = [...new Set([...currentAbsent, ...outPlayerIds])];
      
      if (isBaseballFielding && Object.keys(settings.protectedPositions).length === 0) {
        // Initialize empty protection for all innings
        const initialProtection: { [inning: number]: { [position: string]: string } } = {};
        for (let inning = 1; inning <= teamInnings; inning++) {
          initialProtection[inning] = {};
        }
        onSettingsChange({ 
          protectedPositions: initialProtection,
          absentPlayerIds: newAbsentIds
        });
      } else if (outPlayerIds.length > 0 && newAbsentIds.length !== currentAbsent.length) {
        // Only update if we have new "Out" players to add
        onSettingsChange({ absentPlayerIds: newAbsentIds });
      }
    }
  }, [isOpen, isBaseballFielding, teamInnings, settings.protectedPositions, settings.absentPlayerIds, players, onSettingsChange]);

  const updateProtectedPosition = (inning: number, position: string, playerId: string) => {
    const newProtectedPositions = { ...settings.protectedPositions };
    if (!newProtectedPositions[inning]) {
      newProtectedPositions[inning] = {};
    }
    
    if (playerId === '') {
      delete newProtectedPositions[inning][position];
    } else {
      // Remove this player from any other position in this inning
      Object.keys(newProtectedPositions[inning]).forEach(pos => {
        if (newProtectedPositions[inning][pos] === playerId && pos !== position) {
          delete newProtectedPositions[inning][pos];
        }
      });
      newProtectedPositions[inning][position] = playerId;
    }
    
    onSettingsChange({ protectedPositions: newProtectedPositions });
  };

  const getProtectedPlayerForPosition = (inning: number, position: string): string => {
    return settings.protectedPositions[inning]?.[position] || '';
  };

  const isPlayerProtectedInInning = (inning: number, playerId: string): boolean => {
    const protectedForInning = settings.protectedPositions[inning] || {};
    return Object.values(protectedForInning).includes(playerId);
  };

  const toggleAbsentPlayer = (playerId: string) => {
    const currentAbsent = settings.absentPlayerIds || [];
    const newAbsentIds = currentAbsent.includes(playerId)
      ? currentAbsent.filter(id => id !== playerId)
      : [...currentAbsent, playerId];
    
    // Remove from protected positions if marked as absent
    if (newAbsentIds.includes(playerId)) {
      const newProtectedPositions = { ...settings.protectedPositions };
      Object.keys(newProtectedPositions).forEach(inning => {
        Object.keys(newProtectedPositions[parseInt(inning)]).forEach(position => {
          if (newProtectedPositions[parseInt(inning)][position] === playerId) {
            delete newProtectedPositions[parseInt(inning)][position];
          }
        });
      });
      
      // Also remove from standard protected positions
      const newStandardProtectedPositions = { ...settings.standardProtectedPositions };
      Object.keys(newStandardProtectedPositions).forEach(position => {
        if (newStandardProtectedPositions[position] === playerId) {
          delete newStandardProtectedPositions[position];
        }
      });
      
      onSettingsChange({ 
        absentPlayerIds: newAbsentIds,
        protectedPositions: newProtectedPositions,
        standardProtectedPositions: newStandardProtectedPositions
      });
    } else {
      onSettingsChange({ absentPlayerIds: newAbsentIds });
    }
  };

  const getAvailablePlayersForPosition = (inning: number, position: string): any[] => {
    const currentSelection = getProtectedPlayerForPosition(inning, position);
    const absentIds = settings.absentPlayerIds || [];
    
    return players.filter(player => 
      !absentIds.includes(player.id) && // Not absent
      (player.id === currentSelection || !isPlayerProtectedInInning(inning, player.id))
    );
  };

  // Functions for standard sports (non-baseball)
  const updateStandardProtectedPosition = (position: string, playerId: string) => {
    const newProtectedPositions = { ...settings.standardProtectedPositions };
    
    if (playerId === '') {
      delete newProtectedPositions[position];
    } else {
      // Remove this player from any other position
      Object.keys(newProtectedPositions).forEach(pos => {
        if (newProtectedPositions[pos] === playerId && pos !== position) {
          delete newProtectedPositions[pos];
        }
      });
      newProtectedPositions[position] = playerId;
    }
    
    onSettingsChange({ standardProtectedPositions: newProtectedPositions });
  };

  const getStandardProtectedPlayerForPosition = (position: string): string => {
    return settings.standardProtectedPositions[position] || '';
  };

  const isPlayerStandardProtected = (playerId: string): boolean => {
    return Object.values(settings.standardProtectedPositions).includes(playerId);
  };

  const getAvailablePlayersForStandardPosition = (position: string): any[] => {
    const currentSelection = getStandardProtectedPlayerForPosition(position);
    const absentIds = settings.absentPlayerIds || [];
    
    return players.filter(player => 
      !absentIds.includes(player.id) && // Not absent
      (player.id === currentSelection || !isPlayerStandardProtected(player.id))
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[9999] touch-none"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div 
        className="bg-white rounded-t-xl sm:rounded-xl shadow-xl max-w-4xl w-full animate-slide-up sm:animate-none"
        style={{ 
          maxHeight: 'min(85vh, 85dvh)',
          height: 'auto',
          position: 'relative',
          paddingBottom: 'env(safe-area-inset-bottom, 0)'
        }}
      >
        <div className="flex flex-col h-full max-h-[min(85vh,85dvh)]">
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-stone border-opacity-20 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-primary bg-opacity-10 p-2 rounded-lg">
                <Shuffle className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-dark">{title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-lg transition-colors touch-manipulation"
              disabled={isRandomizing}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content Area */}
          <div 
            className="flex-1 overflow-y-auto min-h-0 overscroll-contain -webkit-overflow-scrolling-touch"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Baseball Fielding Protection Settings */}
            {isBaseballFielding && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-dark flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Set Pitchers & Catchers (Optional)
                  </h4>
                  <button
                    onClick={() => setShowProtectionSettings(!showProtectionSettings)}
                    className="text-primary hover:underline text-xs sm:text-sm"
                  >
                    {showProtectionSettings ? 'Hide' : 'Show'} Protection Settings
                  </button>
                </div>
                
                {showProtectionSettings && (
                  <div className="space-y-4">
                    <p className="text-xs text-gray-600 mb-4">
                      Set specific players as pitchers and catchers for each inning. These positions won't be randomized.
                    </p>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 max-h-48 sm:max-h-60 overflow-y-auto">
                      {Array.from({ length: teamInnings }, (_, i) => i + 1).map(inning => (
                        <div key={inning} className="bg-gray-50 rounded-lg p-2 sm:p-3">
                          <h5 className="font-medium text-xs sm:text-sm text-dark mb-2 sm:mb-3">Inning {inning}</h5>
                          
                          <div className="space-y-2">
                            {/* Pitcher */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Pitcher (P)
                              </label>
                              <select
                                value={getProtectedPlayerForPosition(inning, 'P')}
                                onChange={(e) => updateProtectedPosition(inning, 'P', e.target.value)}
                                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                disabled={isRandomizing}
                              >
                                <option value="">-- Let randomize --</option>
                                {getAvailablePlayersForPosition(inning, 'P').map(player => (
                                  <option key={player.id} value={player.id}>
                                    #{player.number} {player.first_name} {player.last_name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Catcher */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Catcher (C)
                              </label>
                              <select
                                value={getProtectedPlayerForPosition(inning, 'C')}
                                onChange={(e) => updateProtectedPosition(inning, 'C', e.target.value)}
                                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                disabled={isRandomizing}
                              >
                                <option value="">-- Let randomize --</option>
                                {getAvailablePlayersForPosition(inning, 'C').map(player => (
                                  <option key={player.id} value={player.id}>
                                    #{player.number} {player.first_name} {player.last_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Standard Sports Protection Settings */}
            {isStandardSport && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-dark flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Protect Positions (Optional)
                  </h4>
                  <button
                    onClick={() => setShowProtectionSettings(!showProtectionSettings)}
                    className="text-primary hover:underline text-xs sm:text-sm"
                  >
                    {showProtectionSettings ? 'Hide' : 'Show'} Protection Settings
                  </button>
                </div>
                
                {showProtectionSettings && (
                  <div className="space-y-4">
                    <p className="text-xs text-gray-600 mb-4">
                      Set specific players for positions you want to protect from randomization. These positions will keep their assigned players while the rest are randomized.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 max-h-48 sm:max-h-60 overflow-y-auto">
                      {positions.map(position => (
                        <div key={position.key} className="bg-gray-50 rounded-lg p-2 sm:p-3">
                          <h5 className="font-medium text-xs sm:text-sm text-dark mb-2 sm:mb-3">
                            {position.fullName || position.name}
                          </h5>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Protected Player
                            </label>
                            <select
                              value={getStandardProtectedPlayerForPosition(position.key)}
                              onChange={(e) => updateStandardProtectedPosition(position.key, e.target.value)}
                              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                              disabled={isRandomizing}
                            >
                              <option value="">-- Let randomize --</option>
                              {getAvailablePlayersForStandardPosition(position.key).map(player => (
                                <option key={player.id} value={player.id}>
                                  #{player.number} {player.first_name} {player.last_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preview Section - Always shown */}
            {preview && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <h4 className="text-sm font-medium text-dark mb-3 flex items-center">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Randomization Preview
                </h4>
                <div className="space-y-3">
                  <div className="bg-secondary-hover rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Strategy:</span>
                      <span className="font-medium text-dark">{preview.strategy}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="flex items-center justify-center mb-1">
                        <Users className="h-4 w-4 text-green-600 mr-1" />
                      </div>
                      <div className="text-base sm:text-lg font-semibold text-green-700">{preview.willRandomize}</div>
                      <div className="text-xs text-green-600">Will Play</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="flex items-center justify-center mb-1">
                        <Target className="h-4 w-4 text-orange-600 mr-1" />
                      </div>
                      <div className="text-base sm:text-lg font-semibold text-orange-700">{preview.willBench}</div>
                      <div className="text-xs text-orange-600">On Bench</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fair Play Time Setting (only for baseball fielding) */}
            {isBaseballFielding && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-dark">Enforce Bench Rules</label>
                    <p className="text-xs text-gray-600">No back-to-back bench time, max 2 innings on bench</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.ensureFairPlayTime}
                      onChange={(e) => onSettingsChange({ ensureFairPlayTime: e.target.checked })}
                      className="sr-only peer"
                      disabled={isRandomizing}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary peer-focus:ring-opacity-20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            )}

            {/* Warning for Baseball Fielding */}
            {isBaseballFielding && (
              <div className="mx-4 sm:mx-6 mb-6 sm:mb-8 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Target className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600 mt-0.5" />
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h4 className="text-xs sm:text-sm font-medium text-blue-800">Baseball/Softball Fielding Rules</h4>
                    <div className="text-xs text-blue-700 mt-1 space-y-1">
                      <p>• Players rotate across {teamInnings} innings with fair playing time</p>
                      <p>• No player sits on bench for back-to-back innings</p>
                      <p>• No player sits for more than 2 innings total</p>
                      <p>• {preview?.willBench || 0} players will be on bench each inning</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warning for Lineup */}
            {isBaseballLineup && (
              <div className="mx-4 sm:mx-6 mb-6 sm:mb-8 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Users className="h-4 sm:h-5 w-4 sm:w-5 text-green-600 mt-0.5" />
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h4 className="text-xs sm:text-sm font-medium text-green-800">Batting Order</h4>
                    <p className="text-xs text-green-700 mt-1">
                      This will randomly shuffle the batting order. Players not in the starting lineup will remain available as substitutes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning for Standard Sports */}
            {isStandardSport && (
              <div className="mx-4 sm:mx-6 mb-6 sm:mb-8 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <UserCheck className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600 mt-0.5" />
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h4 className="text-xs sm:text-sm font-medium text-blue-800">Protected Positions</h4>
                    <p className="text-xs text-blue-700 mt-1">
                      You can protect specific positions (like QB or C) from randomization. Protected players will stay in their assigned positions while the rest are randomized.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Unavailable Players Section */}
            <div className="p-4 sm:p-6 pt-8 sm:pt-10 border-t border-stone border-opacity-20">
              <h4 className="text-sm font-medium text-dark mb-3 flex items-center">
                <UserX className="h-4 w-4 mr-2 text-red-500" />
                Unavailable Players
              </h4>
              <p className="text-xs text-gray-600 mb-3 sm:mb-4">
                Select players who won't be at the game. They will be excluded from randomization.
              </p>
              
              <div className="space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                {players.map(player => {
                  const isOut = player.injury_status === INJURY_STATUS.OUT;
                  const isUnavailable = (settings.absentPlayerIds || []).includes(player.id);
                  return (
                    <label 
                      key={player.id} 
                      className={`flex items-center gap-2 p-2 sm:p-2.5 rounded hover:bg-gray-50 cursor-pointer ${
                        isOut ? 'bg-red-50 border border-red-200' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isUnavailable}
                        onChange={() => toggleAbsentPlayer(player.id)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500 flex-shrink-0"
                        disabled={isRandomizing || isOut}
                      />
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className={`text-xs sm:text-sm truncate ${isOut ? 'text-red-700 font-medium' : ''}`}>
                          #{player.number} {player.first_name} {player.last_name}
                        </span>
                        {player.injury_status && player.injury_status !== 'H' && (
                          <InjuryBadge 
                            injuryStatus={player.injury_status as InjuryStatus} 
                            size="xs" 
                            className="flex-shrink-0"
                          />
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              
              {(settings.absentPlayerIds || []).length > 0 && (
                <div className="mt-3 p-2 pl-6 sm:pr-12 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700">
                    {(settings.absentPlayerIds || []).length} player(s) marked as unavailable
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="flex space-x-3 p-4 sm:p-6 border-t border-stone border-opacity-20 flex-shrink-0 bg-white">
            <button
              onClick={onClose}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-2 border border-stone border-opacity-20 rounded-xl text-dark hover:bg-cream transition-colors text-sm sm:text-base touch-manipulation min-h-[44px]"
              disabled={isRandomizing}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isRandomizing}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-2 bg-primary text-white rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base touch-manipulation min-h-[44px]"
            >
              {isRandomizing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Randomizing...
                </>
              ) : (
                <>
                  <Shuffle className="h-4 w-4 mr-2" />
                  Randomize
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomizationModal; 